import { TFunction } from 'i18next';
import { forEach } from 'lodash';
import { GetLoginUserprivilege200, UserPrivilege } from '../../../../api/model';

/**
 * 改廃案状態名称取得
 * @param stateNum 改廃案状態
 * @param t 翻訳
 * @returns
 */
export const getTempState = (stateNum: number, t: TFunction) => {
  switch (stateNum) {
    case TempState.MAIKING:
      return t('作成中').toString();
    case TempState.HM_SEND_BACK:
      return t('HM差し戻し').toString();
    case TempState.AWAITING_CONFIRMATION_PS:
      return t('確認待ち(PS機能担当)').toString();
    case TempState.AWAITING_CONFIRMATION_GL:
      return t('確認待ち(G/L)').toString();
    case TempState.AWAITING_APPROVAL_MANAGER:
      return t('承認待ち(管理職)').toString();
    case TempState.APPROVAL_APPLICATION_RETURNED:
      return t('承認申請差し戻し').toString();
    case TempState.APPROVED:
      return t('承認済み').toString();
    case TempState.DELETED:
      return t('削除済み').toString();
    default:
      return '';
  }
};

/**
 * 状態
 */
export const TempState = {
  /** 作成中 */
  MAIKING: 0,
  /** HM差し戻し */
  HM_SEND_BACK: 1,
  /** 確認待ち(PS機能担当) */
  AWAITING_CONFIRMATION_PS: 2,
  /** 確認待ち(G/L) */
  AWAITING_CONFIRMATION_GL: 3,
  /** 承認待ち(管理職) */
  AWAITING_APPROVAL_MANAGER: 4,
  /** 承認申請差し戻し */
  APPROVAL_APPLICATION_RETURNED: 5,
  /** 承認済み */
  APPROVED: 6,
  /** 削除済み */
  DELETED: 7,
};

/**
 * ダイアログ種別名称取得
 * @param dialogType ダイアログ種別
 * @param t 翻訳
 * @returns
 */
export const getDialogTypeStr = (dialogType: number, t: TFunction) => {
  switch (dialogType) {
    case DialogType.STATE_HISTORY:
      return t('状態履歴').toString();
    case DialogType.HM_SUBMISSION:
      return t('HM提出').toString();
    case DialogType.APPLICATION_FOR_CONFIRMATION:
      return t('確認申請').toString();
    case DialogType.CONTENT_CONFIRMATION_GL:
      return t('内容確認').toString();
    case DialogType.CONTENT_CONFIRMATION_MANAGER:
      return t('内容確認').toString();
    case DialogType.CONFIRM:
      return t('確認ダイアログ').toString();
    default:
      return '';
  }
};

export const DialogType = {
  /** 閉じる */
  NONE: -1,
  /** 状態履歴 */
  STATE_HISTORY: 0,
  /** HM提出 */
  HM_SUBMISSION: 1,
  /** 確認申請 */
  APPLICATION_FOR_CONFIRMATION: 2,
  /** 内容確認 */
  CONTENT_CONFIRMATION_GL: 3,
  /** 内容確認 */
  CONTENT_CONFIRMATION_MANAGER: 4,
  /** 確認ダイアログ */
  CONFIRM: 5,
  /** メッセージダイアログ */
  MESSAGE: 6,
  /** 次PS表示確認ダイアログ(確認申請) */
  CONFIRM_CHECK_NEXT_PS: 7,
  /** 次PS表示確認ダイアログ(承認申請) */
  CONFIRM_APPROVE_NEXT_PS: 8,
};

/**
 * 状態およびログインユーザ情報に基づき、操作種別を返却する
 * @param tempState
 * @param loginUserInfo
 * @returns
 */
export function getDialogTypeFromLoginInfo(
  tempState: number | null | undefined,
  loginUserInfo: GetLoginUserprivilege200 | null
): number {
  // ログインユーザが原作担当、かつPS会廃案の状態が「0：作成中」「1：HM差し戻し」の時に「HM提出」表示
  if (
    (tempState === TempState.MAIKING || tempState === TempState.HM_SEND_BACK) &&
    (loginUserInfo?.privilege_id?.indexOf(9) ?? -1) >= 0
  ) {
    return DialogType.HM_SUBMISSION;
  }
  // ログインユーザが原作担当以外、かつPS会廃案の状態が「0：作成中」「2：確認待ち(PS機能担当)」「5：承認申請差し戻し」の時に「確認申請」表示
  if (
    (tempState === 0 || tempState === 2 || tempState === 5) &&
    (loginUserInfo?.privilege_id?.indexOf(9) ?? -1) <= -1
  ) {
    return DialogType.APPLICATION_FOR_CONFIRMATION;
  }
  // ログインユーザーが「PS一覧-承認機能(確認者) 権限」を持ち、かつPS改廃案の状態が「2:確認待ち(G/L)」の時に表示。
  if (
    tempState === 3 &&
    ((loginUserInfo?.privilege_id?.indexOf(3) ?? -1) >= 0 || (loginUserInfo?.privilege_id?.indexOf(1) ?? -1) >= 0)
  ) {
    return DialogType.CONTENT_CONFIRMATION_GL;
  }
  // ログインユーザーが「PS一覧-承認機能 権限」を持ち、かつPS改廃案の状態が「4:承認待ち(管理職)」の時に表示。
  if (
    tempState === 4 &&
    ((loginUserInfo?.privilege_id?.indexOf(2) ?? -1) >= 0 || (loginUserInfo?.privilege_id?.indexOf(1) ?? -1) >= 0)
  ) {
    return DialogType.CONTENT_CONFIRMATION_MANAGER;
  }
  // いずれにも該当しない場合
  return -1;
}

/**
 * 状態に基づいてダイアログ判別
 * @param tempstate 状態
 * @returns ダイアログ種別
 */
export function getDialogTypeFromTempStateForViewMode(tempstate: number): number {
  switch (tempstate) {
    case TempState.AWAITING_CONFIRMATION_PS:
      return DialogType.HM_SUBMISSION;
    case TempState.HM_SEND_BACK:
    case TempState.AWAITING_CONFIRMATION_GL:
      return DialogType.APPLICATION_FOR_CONFIRMATION;
    case TempState.AWAITING_APPROVAL_MANAGER:
      return DialogType.CONTENT_CONFIRMATION_GL;
    case TempState.APPROVAL_APPLICATION_RETURNED:
      return DialogType.CONTENT_CONFIRMATION_GL;
    case TempState.APPROVED:
      return DialogType.CONTENT_CONFIRMATION_MANAGER;
    case TempState.MAIKING:
    case TempState.DELETED:
    default:
      return DialogType.NONE;
  }
}

/**
 * 画像マーク種別
 * @param tempState
 * @param loginUserInfo
 * @returns
 */
export function getCheckMarkType(
  tempState: number | null | undefined,
  loginUserInfo: GetLoginUserprivilege200 | null
): string {
  const state = getDialogTypeFromLoginInfo(tempState, loginUserInfo);
  switch (state) {
    case DialogType.STATE_HISTORY:
    case DialogType.HM_SUBMISSION:
    case DialogType.APPLICATION_FOR_CONFIRMATION:
      return 'fa-file-arrow-up';
    case DialogType.CONTENT_CONFIRMATION_GL:
    case DialogType.CONTENT_CONFIRMATION_MANAGER:
      return 'fa-circle-check';
    default:
      return '';
  }
}

/**
 * 画像マーク種別
 * @param tempState
 * @param loginUserInfo
 * @returns
 */
export function getCheckMarkTypeFromDialogType(dialogType: number | null | undefined): string {
  switch (dialogType) {
    case DialogType.STATE_HISTORY:
    case DialogType.HM_SUBMISSION:
    case DialogType.APPLICATION_FOR_CONFIRMATION:
      return 'fa-file-arrow-up';
    case DialogType.CONTENT_CONFIRMATION_GL:
    case DialogType.CONTENT_CONFIRMATION_MANAGER:
      return 'fa-circle-check';
    default:
      return '';
  }
}

/**
 * 日付の時刻を切り捨てる(00:00:00にする)
 * @param date 対象日付
 */
export function truncateDate(date: Date): Date {
  if (date) {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
  return date;
}

/**
 * 日付フォーマット
 * @param date
 * @param format
 * @returns
 */
export function formatDate(date: Date, format: string): string {
  let formatStr = format;
  if (!format) formatStr = 'YYYY-MM-DD hh:mm:ss.SSS';

  formatStr = formatStr.replace(/YYYY/g, date.getFullYear().toString());
  formatStr = formatStr.replace(/YY/g, date.getFullYear().toString().slice(2));
  formatStr = formatStr.replace(/MM/g, `0${date.getMonth() + 1}`.slice(-2));
  formatStr = formatStr.replace(/DD/g, `0${date.getDate()}`.slice(-2));
  formatStr = formatStr.replace(/hh/g, `0${date.getHours()}`.slice(-2));
  formatStr = formatStr.replace(/mm/g, `0${date.getMinutes()}`.slice(-2));
  formatStr = formatStr.replace(/ss/g, `0${date.getSeconds()}`.slice(-2));
  if (formatStr.match(/S/g)) {
    const milliSeconds = `00${date.getMilliseconds()}`.slice(-3);
    const count = formatStr.match(/S/g)?.length ?? 0;
    for (let i = 0; i < count; i += 1) formatStr = formatStr.replace(/S/, milliSeconds.substring(i, i + 1));
  }
  return formatStr;
}

/**
 * (yyyy-mm-dd形式の文字列をyyyy/mm/dd形式に変換)
 * @param dateStr
 * @returns
 */
export function getFormatDateYYYYMMDD(dateStr: string | undefined): string {
  if (dateStr === undefined) {
    return '';
  }
  if (dateStr.length < 10) {
    return dateStr;
  }
  const subStr = dateStr.substring(0, 10).replaceAll('-', '/');
  return subStr;
}

/**
 * (yyyy-mm-dd形式の文字列をyy/mm/dd形式に変換)
 * @param dateStr
 * @returns
 */
export function getFormatDateYYMMDD(dateStr: string | undefined): string {
  if (dateStr === undefined) {
    return '';
  }
  if (dateStr.length < 10) {
    return dateStr;
  }
  const subStr = dateStr.substring(2, 10).replaceAll('-', '/');
  return subStr;
}

/**
 * 状態
 */
export const Message = {
  /** 図面リストの出力に失敗しました。管理者に打ち上げください。 */
  OUTPUT_ERROR_DRAWING_LIST: 0,
  /** PS改廃案を削除してもよろしいですか? */
  CONFIRM_DELETE_PS_TEMPORARY: 1,
  /** PS改廃案を削除しました。 */
  SUCCESS_DELETE_PS_TEMPORARY: 2,
  /** PS改廃案の削除に失敗しました。管理者に打ち上げください。 */
  FAILURE_DELETE_PS_TEMPORARY: 3,
  /** PS改廃案の削除ができません。 */
  CANNOT_DELETE_PS_TEMPORARY: 4,
  /** 差し戻しを行なってもよろしいですか？ */
  CONFIRM_BACK_PS_TEMPORARY: 5,
  /** PS改廃案を差し戻しました。 */
  SUCCESS_BACK_PS_TEMPORARY: 6,
  /** PS改廃案の差し戻しに失敗しました。管理者に打ち上げください。 */
  FAILURE_BACK_PS_TEMPORARY: 7,
  /** PS改廃案をHMに提出してもよろしいですか？ */
  CONFIRM_HM_SUBMIT_PS_TEMPORARY: 8,
  /** 確認申請を行なってもよろしいですか？ */
  CONFIRM_CHECK_ADD_PS_TEMPORARY: 9,
  /** 確認(G/L)を行なってもよろしいですか？ */
  CONFIRM_CHECK_PS_TEMPORARY: 10,
  /** 承認を行なってもよろしいですか？ */
  CONFIRM_APPROVED_PS_TEMPORARY: 11,
  /** PS改廃案を承認しました。 */
  SUCCESS_APPROVED_PS_TEMPORARY: 12,
  /** PS改廃案の承認に失敗しました。管理者に打ち上げください。 */
  FAILURE_APPROVED_PS_TEMPORARY: 13,
  /** {項目名}を入力してください。 */
  ERROR_REQUIRED_INPUT_ITEM: 14,
  /** PS改廃案を提出しました。 */
  SUCCESS_HM_SUBMIT_PS_TEMPORARY: 15,
  /** PS改廃案の提出に失敗しました。管理者に打ち上げください。 */
  FAILURE_HM_SUBMIT_PS_TEMPORARY: 16,
  /** 変更の理由を入力してください。 */
  VALIDATE_INPUT_RELEASE_NOTE: 17,
  /** 差戻の理由を入力してください。 */
  VALIDATE_INPUT_COMMENT: 18,
  /** 変更の理由、差戻の理由を入力してください。 */
  VALIDATE_INPUT_RELEASE_NOTE_AND_COMMENT: 19,
  /** 差戻の理由を入力してください。 */
  VALIDATE_INPUT_COMMENT_CHECK_OR_APPROVED: 20,
  /** PS改廃案の確認申請に失敗しました。管理者に打ち上げください。 */
  FAILURE_CHECK_ADD_PS_TEMPORARY: 21,
  /** PS改廃案の確認に失敗しました。管理者に打ち上げください。 */
  FAILURE_CHECK_PS_TEMPORARY: 22,
  /** PS改廃案を確認申請しました。 */
  SUCCESS_CHECK_ADD_PS_TEMPORARY: 23,
  /** PS改廃案を確認しました。 */
  SUCCESS_CHECK_PS_TEMPORARY: 24,
  /** PS確認待ちはありません。 */
  NO_NEXT_CHECK_PS: 25,
  /** PS承認待ちはありません。 */
  NO_NEXT_APPROVE_PS: 26,
  /** 次のPSを表示しますか？ */
  CONFIRM_NEXT_PS: 27,
  /** 初期値 */
  DEFAULT: -1,
};

/**
 * PS一覧、改廃案一覧ダイアログメッセージ取得
 * @param messageNum ダイアログ種別
 * @param t 翻訳
 * @returns
 */
export const getDialogMessage = (messageNum: number, t: TFunction, varStr = '') => {
  switch (messageNum) {
    case Message.OUTPUT_ERROR_DRAWING_LIST:
      return `${t('図面リストの出力に失敗しました。管理者に打ち上げください。').toString()}\n${varStr}`;
    case Message.CONFIRM_DELETE_PS_TEMPORARY:
      return t('PS改廃案を削除してもよろしいですか?').toString();
    case Message.SUCCESS_DELETE_PS_TEMPORARY:
      return t('PS改廃案を削除しました。').toString();
    case Message.FAILURE_DELETE_PS_TEMPORARY:
      return `${t('PS改廃案の削除に失敗しました。管理者に打ち上げください。').toString()}\n${varStr}`;
    case Message.CANNOT_DELETE_PS_TEMPORARY:
      return `${t('指定された改廃案は申請中のため削除出来ません。').toString()}\n${varStr}`;
    case Message.CONFIRM_BACK_PS_TEMPORARY:
      return t('差し戻しを行なってもよろしいですか？').toString();
    case Message.SUCCESS_BACK_PS_TEMPORARY:
      return t('PS改廃案を差し戻しました。').toString();
    case Message.FAILURE_BACK_PS_TEMPORARY:
      return `${t('PS改廃案の差し戻しに失敗しました。').toString()}\n${varStr}`;
    case Message.CONFIRM_HM_SUBMIT_PS_TEMPORARY:
      return t('PS改廃案をHMに提出してもよろしいですか？').toString();
    case Message.CONFIRM_CHECK_ADD_PS_TEMPORARY:
      return t('確認申請を行なってもよろしいですか？').toString();
    case Message.CONFIRM_CHECK_PS_TEMPORARY:
      return t('確認(G/L)を行なってもよろしいですか？').toString();
    case Message.CONFIRM_APPROVED_PS_TEMPORARY:
      return t('承認を行なってもよろしいですか？').toString();
    case Message.SUCCESS_APPROVED_PS_TEMPORARY:
      return t('PS改廃案を承認しました。').toString();
    case Message.FAILURE_APPROVED_PS_TEMPORARY:
      return `${t('PS改廃案の承認に失敗しました。管理者に打ち上げください。').toString()}\n${varStr}`;
    case Message.ERROR_REQUIRED_INPUT_ITEM:
      return varStr; // メッセージは外から渡す。
    case Message.SUCCESS_HM_SUBMIT_PS_TEMPORARY:
      return t('PS改廃案を提出しました。').toString();
    case Message.FAILURE_HM_SUBMIT_PS_TEMPORARY:
      return `${t('PS改廃案の提出に失敗しました。管理者に打ち上げください。').toString()}\n${varStr}`;
    case Message.VALIDATE_INPUT_RELEASE_NOTE:
      return `${t('変更の理由を入力してください。').toString()}`;
    case Message.VALIDATE_INPUT_COMMENT:
      return `${t('差戻の理由を入力してください。').toString()}`;
    case Message.VALIDATE_INPUT_RELEASE_NOTE_AND_COMMENT:
      return `${t('変更の理由、差戻の理由を入力してください。').toString()}`;
    case Message.VALIDATE_INPUT_COMMENT_CHECK_OR_APPROVED:
      return `${t('差戻の理由を入力してください。').toString()}`;
    case Message.FAILURE_CHECK_ADD_PS_TEMPORARY:
      return `${t('PS改廃案の確認申請に失敗しました。管理者に打ち上げください。').toString()}`;
    case Message.FAILURE_CHECK_PS_TEMPORARY:
      return `${t('PS改廃案の確認に失敗しました。管理者に打ち上げください。').toString()}`;
    case Message.SUCCESS_CHECK_ADD_PS_TEMPORARY:
      return `${t('PS改廃案を確認申請しました。').toString()}`;
    case Message.SUCCESS_CHECK_PS_TEMPORARY:
      return `${t('PS改廃案を確認しました。').toString()}`;
    case Message.NO_NEXT_CHECK_PS:
      return `${t('PS確認待ちはありません。').toString()}`;
    case Message.NO_NEXT_APPROVE_PS:
      return `${t('PS承認待ちはありません。').toString()}`;
    case Message.CONFIRM_NEXT_PS:
      return `${t('次のPSを表示しますか？').toString()}`;
    default:
      return '';
  }
};

/**
 * アクセス制限を確認し、フルアクセス権限かつ編集/閲覧可の場合true
 * @param userPriList
 * @returns
 */
export function getIsAccess(userPriList: UserPrivilege[]) {
  if (userPriList.length <= 0) {
    return false;
  }
  let isAccess = false;
  forEach(userPriList, (userPri) => {
    // 1件でもフルアクセス権限かつ編集/閲覧可があればtrue
    if (userPri.accsess_restriction === 0 && userPri.privilege_setting === 0) {
      isAccess = true;
    }
  });
  return isAccess;
}

/**
 * ユーザーレベル
 */
export const UserType = {
  /** システム管理者 */
  ADMINISTRATOR: 1,
  /** 承認者（管理職） */
  RECOGNIZER: 2,
  /** 確認者（G/L） */
  CONFIRMING_PERSON: 3,
};
